import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { runCompetitionOperation } from "../../competitionOperation"

// `fallback` words a code the map does not know: by default an operation whose result is still open; Action Logs pass their own,
// because a logged failure is a refusal that is already final.
export const phaseMessage = (code, fallback = "The result could not be confirmed. Check the same request before starting another action.") => ({
  PHASE_REVIEW_REQUIRED: "Review the current phase from Phases or Main Draw before changing its matches.",
  PHASE_OPERATION_IN_PROGRESS: "A phase operation is still being completed. Check its result before starting another action.",
  SUPERADMIN_REQUIRED: "Only a SuperAdmin can delete a phase.",
  PHASE_QUALIFIERS_MISSING: "There are not enough real qualifiers. Complete the group entries or edit the qualifier quota before generating.",
  PHASE_PROVISIONAL_CONFIRMATION_REQUIRED: "Confirm the provisional selection before generating the phase.",
  PHASE_RESET_CONFIRMATION_REQUIRED: "Review the reset impact. A reason is required when recorded results are discarded.",
  ERROR_PERMISSIONS: "Your account no longer has permission for this action.",
  PHASE_REVISION_CONFLICT: "The competition or its matches changed. Reload and review the current phase before trying again.",
  PHASE_ALREADY_EXISTS: "This competition already has an additional phase.",
  PHASE_REQUIRES_GROUPS: "An additional phase is available only for a competition with groups.",
  PHASE_POINTS_FINALIZED: "The phase points have been finalized. Its configuration and matches can no longer be reset or deleted.",
  PHASE_LEGACY_CONFIGURATION: "This historical configuration cannot be changed with this editor.",
  PHASE_QUOTA_ABOVE_CAPACITY: "The phase admits more qualifiers than the competition's Main List capacity. Increase the capacity in competition details, then edit the phase.",
  PHASE_COMPETITION_NOT_GROUPS: "This competition no longer uses groups. Restore the groups format in competition details, or ask a SuperAdmin to delete the phase.",
  PHASE_GROUPS_CHANGED: "The group matches were drawn for a different number of groups. Generate the group draw again before generating the phase.",
  PHASE_TARGET_NOT_FOUND: "This competition or phase is no longer available.",
  INVALID_PHASE_QUOTA: "Enter a whole number of qualifiers within the competition limits.",
  INVALID_PHASE_RANKING: "Select Fixed or Dynamic inter-group ranking.",
  TENIS10_QUALIFIERS_PER_GROUP: "A Tenis10 phase takes the same number of children from every group. Edit the phase before generating it.",
  // Tudor 2026-10-29 (variant B): tenis10.ro's brackets take 2, 3 or 4 children of a single group.
  TENIS10_ONE_GROUP_QUALIFIERS: "A Tenis10 phase takes 2, 3 or 4 children of a single group: tenis10.ro has no bracket for more. Edit the phase before generating it.",
  // The api refuses a bracket that cannot keep one group's players apart (an adult phase of one group), instead of retrying it
  // (PROD 2026-10-29).
  PHASE_BRACKET_CONFLICT_RESOLUTION_STALLED: "No bracket keeps the players of one group apart in the first round for these qualifiers, so the phase cannot be drawn. Review the number of groups and qualifiers.",
  TENIS10_RANKING_FIXED: "A Tenis10 phase takes the first children of every group, with no inter-group ranking. Edit the phase before generating it.",
  INVALID_PHASE_DETAILS: "The phase settings do not match the competition. Reload the phase and review its settings.",
  INVALID_PHASE_ACTION: "This phase action is not available. Reload the phase.",
  PHASE_OPERATION_CONFLICT: "This request belongs to a different action. Recover its result before trying again.",
  PHASE_OPERATION_NOT_RECEIVED: "The request was not received. Reload the current phase before trying again.",
  INVALID_PHASE_OPERATION: "This request could not be identified, so nothing was changed. Start the action again.",
}[code] || fallback)

export default function usePhaseOperation({ eventId, competitionId, phaseId, userId }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = `phase-editor:${userId}:${eventId}:${competitionId}`
  const active = useRef(null)
  const [state, setState] = useState({})
  // `firstSubmission` marks the first send of a phase command; Check result and re-observation after a reload leave it false.
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.runner.cancel()
    const runner = runCompetitionOperation({ operation, socket, submit, firstSubmission, operationKind: "phase-managed", messageForCode: phaseMessage,
      read: operationId => get("/admin/phase-operations/" + operationId) })
    const request = { operation, runner }
    active.current = request
    setState({ status: "pending", operation })
    return runner.promise.then(result => {
      if (active.current !== request || result.status === "cancelled") return
      setState({ ...result, operation })
      if (["saved", "error"].includes(result.status)) {
        active.current = null
        try { sessionStorage.removeItem(key) } catch { /* A retained identity only recovers the same operation. */ }
      }
      return result
    })
  }, [key, socket])
  useEffect(() => {
    setState({})
    if (userId) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(key) || "null")
        if (saved?.eventId === eventId && saved.competitionId === competitionId
          && saved.operationId && saved.correlationId) observe(saved)
      } catch { /* Malformed recovery records never submit a command. */ }
    }
    return () => { active.current?.runner.cancel(); active.current = null }
  }, [eventId, competitionId, phaseId, userId, key, observe])
  const save = (action, revision, details, confirmation = {}) => {
    if (active.current) return
    if (!userId) { setState({ status: "error", message: "Your signed-in account could not be identified. Reload before saving." }); return }
    const operation = { eventId, competitionId, phaseId, action, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId() }
    try { sessionStorage.setItem(key, JSON.stringify(operation)) } catch {
      setState({ status: "error", message: "Enable browser session storage so the result can be recovered after reload." }); return
    }
    return observe(operation, () => post(`/admin/events/${eventId}/competitions/${competitionId}/phase-operations`,
      { action, phaseId, operationId: operation.operationId, revision, details, ...confirmation, socketClientId },
      { headers: { "X-Correlation-Id": operation.correlationId } }), true)
  }
  const recover = () => {
    const operation = active.current?.operation || state.operation
    if (operation) observe(operation, () => post(`/admin/phase-operations/${operation.operationId}/retry`,
      { socketClientId }, { headers: { "X-Correlation-Id": operation.correlationId } }))
  }
  return { ...state, pending: ["pending", "unknown"].includes(state.status), save, recover }
}
